import { Modal, SvgExpandModal, SvgMinimiseModal } from '@ui-kit'
import {
  ReactNode,
  useState,
  useRef,
  useLayoutEffect,
  MouseEvent,
  KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'

export interface PopoutableListingShellProps {
  isOpen: boolean
  onClose: () => void
  title: string
  headerActions?: ReactNode
  children: ReactNode
  /** Disable focus trap when a nested modal (Add/Edit/Assign) is open on top. */
  disableFocusTrap?: boolean
}

export interface ListingPopoutExpandButtonProps {
  onClick: () => void
  disabled?: boolean
  tabIndex?: number
}

/**
 * Expand control for accordion headers. Stops propagation so the accordion
 * does not toggle when opening the listing popout.
 */
export const ListingPopoutExpandButton = ({
  onClick,
  disabled = false,
  tabIndex,
}: ListingPopoutExpandButtonProps) => {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!disabled) {
      onClick()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      if (!disabled) {
        onClick()
      }
    }
  }

  return (
    <button
      type="button"
      className="flex items-center justify-center p-1 disabled:opacity-40 disabled:cursor-not-allowed"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      tabIndex={tabIndex}
      aria-label="Expand table"
    >
      <SvgExpandModal />
    </button>
  )
}

/**
 * Clears only the grid root height CustomAgTable sets via ResizeObserver.
 * Do not clear other ag-* nodes (e.g. .ag-header) — that collapses column headers.
 */
const clearAgGridInlineHeights = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>('.ag-root-wrapper').forEach((el) => {
    el.style.height = ''
  })
}

/**
 * Renders children once into a stable portal host. Opening the modal moves that
 * same DOM node via appendChild so ListingTable filter/sort/pagination state
 * is preserved (swapping createPortal containers would remount children).
 */
export const PopoutableListingShell = ({
  isOpen,
  onClose,
  title,
  headerActions,
  children,
  disableFocusTrap = false,
}: PopoutableListingShellProps) => {
  const [inlineHost, setInlineHost] = useState<HTMLElement | null>(null)
  const [modalHost, setModalHost] = useState<HTMLElement | null>(null)
  const savedInlineHeightRef = useRef<number | null>(null)
  const wasOpenRef = useRef(false)

  const stableHostRef = useRef<HTMLDivElement | null>(null)
  if (!stableHostRef.current) {
    const el = document.createElement('div')
    el.className = 'w-full'
    stableHostRef.current = el
  }
  const stableHost = stableHostRef.current

  useLayoutEffect(() => {
    // Modal needs h-full to fill the 90% viewport; inline must not keep it or
    // the accordion table stretches and breaks layout after minimise.
    stableHost.className = isOpen ? 'w-full h-full min-h-0' : 'w-full'

    // Snapshot height while still in the accordion, before moving into the modal.
    if (
      isOpen &&
      !wasOpenRef.current &&
      inlineHost &&
      stableHost.parentElement === inlineHost
    ) {
      const height = stableHost.offsetHeight
      if (height > 0) {
        savedInlineHeightRef.current = height
      }
      stableHost.style.height = ''
    }

    const parent = isOpen && modalHost ? modalHost : inlineHost
    if (parent && stableHost.parentElement !== parent) {
      parent.appendChild(stableHost)
    }

    // Restore pre-popout height and clear AG Grid's modal-sized pixel heights.
    if (!isOpen && wasOpenRef.current && inlineHost) {
      clearAgGridInlineHeights(stableHost)
      if (savedInlineHeightRef.current != null) {
        stableHost.style.height = `${savedInlineHeightRef.current}px`
      }
      window.dispatchEvent(new Event('resize'))
    }

    if (isOpen) {
      stableHost.style.height = ''
    }

    wasOpenRef.current = isOpen
  }, [isOpen, modalHost, inlineHost, stableHost])

  useLayoutEffect(() => {
    return () => {
      stableHost.remove()
    }
  }, [stableHost])

  return (
    <>
      <div
        ref={setInlineHost}
        className={isOpen ? 'min-h-8 w-full' : 'w-full'}
      />
      <Modal
        isOpen={isOpen}
        closeModal={onClose}
        showCloseButton={false}
        width="w-[90%] h-[90%]"
        closeModalOnOutsideClick={false}
        bodyCssClassName="!flex !flex-col !p-4"
        allowOutsideClick={true}
        minResizeHeight={250}
        minResizeWidth={520}
        disableFocusTrap={disableFocusTrap}
        isResizable
      >
        <div className="flex flex-col gap-3 h-full min-h-0 w-full">
          <div className="flex justify-between items-center shrink-0 py-1">
            <span className="text-sm font-medium font-primary">{title}</span>
            <span className="flex flex-row items-center gap-2">
              {headerActions}
              <button
                type="button"
                onClick={onClose}
                aria-label="Minimise table"
                className="flex items-center justify-center p-1"
              >
                <SvgMinimiseModal />
              </button>
            </span>
          </div>
          <div
            ref={setModalHost}
            className="flex-1 min-h-0 overflow-auto w-full"
          />
        </div>
      </Modal>
      {createPortal(children, stableHost)}
    </>
  )
}

export default PopoutableListingShell
